import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@tanstack/react-query';
import { HStack } from '@astryxdesign/core/HStack';
import { VStack } from '@astryxdesign/core/VStack';
import { Text } from '@astryxdesign/core/Text';
import { Switch } from '@astryxdesign/core/Switch';
import { Selector } from '@astryxdesign/core/Selector';
import { MultiSelector } from '@astryxdesign/core/MultiSelector';
import { NumberInput } from '@astryxdesign/core/NumberInput';
import { TextInput } from '@astryxdesign/core/TextInput';
import { alertChannels, alertRuleInput, personalRuleInput } from '@qronstat/shared';
import type {
  AlertChannel,
  AlertCondition,
  AlertDefinition,
  AlertWindow,
  FilterNode,
} from '@qronstat/shared';
import { ApiError } from '../../api/client';
import { useApp } from '../../app/context';
import { DialogForm } from '../DialogForm';
import { ConditionBuilder } from '../filters/ConditionBuilder';
import { cubesOf, isComplete } from '../filters/model';
import { formatValue } from '../../model/format';
import { text } from '../../model/meta';
import { useSelectedText } from '../useSelectedText';

export interface RuleDraft {
  name: string;
  template: string | null;
  definition: AlertDefinition;
  enabled: boolean;
  cooldownMinutes: number;
  notifyRecovery: boolean;
  channels?: AlertChannel[];
}

const operators = ['gt', 'gte', 'lt', 'lte', 'eq'] as const;

export function RuleEditor({
  title,
  initial,
  mode,
  onClose,
  onSubmit,
}: {
  title: string;
  initial: RuleDraft;
  mode: 'team' | 'personal';
  onClose: () => void;
  onSubmit: (draft: RuleDraft) => Promise<unknown>;
}) {
  const intl = useIntl();
  const selectedText = useSelectedText();
  const { meta, locale } = useApp();
  const [draft, setDraft] = useState<RuleDraft>(initial);
  const [error, setError] = useState<string | null>(null);
  const def = draft.definition;
  const setDef = (patch: Partial<AlertDefinition>) =>
    setDraft((d) => ({ ...d, definition: { ...d.definition, ...patch } }));
  const metric = meta?.metric(def.metric);
  const metricOptions = useMemo(
    () =>
      (meta?.meta.metrics ?? [])
        .filter((m) => !m.hidden && !m.derived && !m.technical)
        .map((m) => ({ value: m.id, label: text(m.title, locale) })),
    [meta, locale],
  );
  const groupOptions = useMemo(
    () =>
      (meta?.meta.dimensions ?? [])
        .filter(
          (d) =>
            !d.hidden &&
            d.type === 'string' &&
            metric &&
            meta?.meta.facts.some((f) => f.cube === metric.cube),
        )
        .map((d) => ({ value: d.id, label: text(d.title, locale) })),
    [meta, metric, locale],
  );
  const save = useMutation({
    mutationFn: (body: RuleDraft) => onSubmit(body),
    onSuccess: () => onClose(),
    onError: (e) => {
      if (e instanceof ApiError && e.details.reason === 'window_now_requires_live_metric')
        setError(intl.formatMessage({ id: 'alerts.error.nowNeedsLive' }));
      else if (e instanceof ApiError && e.details.notJoinable)
        setError(intl.formatMessage({ id: 'alerts.error.notJoinable' }));
      else setError(intl.formatMessage({ id: 'common.saveFailed' }));
    },
  });
  const parsed =
    mode === 'team'
      ? alertRuleInput.safeParse(draft)
      : personalRuleInput.safeParse({
          name: draft.name,
          template: draft.template,
          definition: draft.definition,
          enabled: draft.enabled,
          cooldownMinutes: draft.cooldownMinutes,
          notifyRecovery: draft.notifyRecovery,
        });
  const valid = parsed.success && ((def.filters ?? []) as FilterNode[]).every(isComplete);
  const windowKind = def.window.kind;
  const condition = def.condition;
  return (
    <DialogForm
      title={title}
      onClose={onClose}
      onSubmit={() => save.mutate(draft)}
      submitting={save.isPending}
      valid={valid}
      error={error}
      width={680}
    >
      <TextInput
        label={intl.formatMessage({ id: 'alerts.ruleName' })}
        value={draft.name}
        onChange={(v) => setDraft((d) => ({ ...d, name: v }))}
      />
      <Selector
        label={intl.formatMessage({ id: 'alerts.metric' })}
        hasSearch
        value={def.metric}
        onChange={(v) => v && setDef({ metric: v })}
        options={metricOptions}
      />
      <Selector
        label={intl.formatMessage({ id: 'alerts.groupBy' })}
        description={intl.formatMessage({ id: 'alerts.groupByHint' })}
        hasSearch
        hasClear
        value={def.groupBy ?? null}
        onChange={(v) => setDef(v ? { groupBy: v, labelBy: v } : { groupBy: undefined, labelBy: undefined })}
        options={groupOptions}
      />
      <HStack gap={3} wrap="wrap" vAlign="end">
        <Selector
          label={intl.formatMessage({ id: 'alerts.window' })}
          value={windowKind}
          onChange={(v) => {
            const w: AlertWindow =
              v === 'minutes'
                ? { kind: 'minutes', minutes: 60 }
                : v === 'now'
                  ? { kind: 'now' }
                  : { kind: 'today' };
            setDef({ window: w });
          }}
          options={(['minutes', 'today', 'now'] as const).map((k) => ({
            value: k,
            label: intl.formatMessage({ id: `alerts.window.${k}` }),
            ...(k === 'now' && !metric?.live ? { disabled: true } : {}),
          }))}
        />
        {def.window.kind === 'minutes' ? (
          <NumberInput
            label={intl.formatMessage({ id: 'alerts.minutes' })}
            value={def.window.minutes}
            onChange={(v: number) =>
              setDef({ window: { kind: 'minutes', minutes: Math.max(5, Math.round(v)) } })
            }
          />
        ) : null}
      </HStack>
      <HStack gap={3} wrap="wrap" vAlign="end">
        <Selector
          label={intl.formatMessage({ id: 'alerts.condition' })}
          value={condition.kind}
          onChange={(v) => {
            const c: AlertCondition =
              v === 'deviation'
                ? { kind: 'deviation', direction: 'below', percent: 30, weeks: 4 }
                : v === 'no_activity'
                  ? { kind: 'no_activity', weeks: 4 }
                  : { kind: 'threshold', operator: 'gt', value: 0 };
            setDef({ condition: c });
          }}
          options={(['threshold', 'deviation', 'no_activity'] as const).map((k) => ({
            value: k,
            label: intl.formatMessage({ id: `alerts.condition.${k}` }),
          }))}
        />
        {condition.kind === 'threshold' ? (
          <>
            <Selector
              label={intl.formatMessage({ id: 'filters.operator' })}
              value={condition.operator}
              onChange={(v) =>
                v && setDef({ condition: { ...condition, operator: v as (typeof operators)[number] } })
              }
              options={operators.map((o) => ({
                value: o,
                label: intl.formatMessage({ id: `filters.op.${o === 'eq' ? 'equals' : o}` }),
              }))}
            />
            <NumberInput
              label={intl.formatMessage({ id: 'alerts.value' })}
              description={metric ? formatValue(condition.value, metric.format, locale) : undefined}
              value={condition.value}
              onChange={(v: number) => setDef({ condition: { ...condition, value: v } })}
            />
          </>
        ) : null}
        {condition.kind === 'deviation' ? (
          <>
            <Selector
              label={intl.formatMessage({ id: 'alerts.direction' })}
              value={condition.direction}
              onChange={(v) =>
                v && setDef({ condition: { ...condition, direction: v as 'below' | 'above' } })
              }
              options={[
                { value: 'below', label: intl.formatMessage({ id: 'alerts.direction.below' }) },
                { value: 'above', label: intl.formatMessage({ id: 'alerts.direction.above' }) },
              ]}
            />
            <NumberInput
              label={intl.formatMessage({ id: 'alerts.percent' })}
              value={condition.percent}
              onChange={(v: number) => setDef({ condition: { ...condition, percent: Math.max(1, v) } })}
            />
            <NumberInput
              label={intl.formatMessage({ id: 'alerts.weeks' })}
              value={condition.weeks}
              onChange={(v: number) =>
                setDef({ condition: { ...condition, weeks: Math.min(12, Math.max(1, Math.round(v))) } })
              }
            />
          </>
        ) : null}
        {condition.kind === 'no_activity' ? (
          <NumberInput
            label={intl.formatMessage({ id: 'alerts.weeks' })}
            value={condition.weeks}
            onChange={(v: number) =>
              setDef({ condition: { ...condition, weeks: Math.min(12, Math.max(1, Math.round(v))) } })
            }
          />
        ) : null}
      </HStack>
      <VStack gap={2}>
        <Text weight="semibold">{intl.formatMessage({ id: 'filters.conditions' })}</Text>
        <ConditionBuilder
          scope={cubesOf(meta, def.metric)}
          value={(def.filters ?? []) as FilterNode[]}
          onChange={(f) => setDef({ filters: f })}
          narrow
        />
      </VStack>
      <HStack gap={3} wrap="wrap" vAlign="end">
        <Switch
          label={intl.formatMessage({ id: 'alerts.activeHours' })}
          value={Boolean(def.activeHours)}
          onChange={(v) =>
            setDef(v ? { activeHours: { from: '09:00', to: '23:00' } } : { activeHours: undefined })
          }
        />
        {def.activeHours ? (
          <>
            <TextInput
              label={intl.formatMessage({ id: 'filters.from' })}
              value={def.activeHours.from}
              onChange={(v) => setDef({ activeHours: { from: v, to: def.activeHours?.to ?? '23:00' } })}
            />
            <TextInput
              label={intl.formatMessage({ id: 'filters.to' })}
              value={def.activeHours.to}
              onChange={(v) => setDef({ activeHours: { from: def.activeHours?.from ?? '09:00', to: v } })}
            />
          </>
        ) : null}
      </HStack>
      <Switch
        label={intl.formatMessage({ id: 'alerts.respectSchedule' })}
        value={Boolean(def.respectSchedule)}
        onChange={(v) => setDef({ respectSchedule: v })}
      />
      <Switch
        label={intl.formatMessage({ id: 'filters.realOnly' })}
        value={def.realOnly !== false}
        onChange={(v) => setDef({ realOnly: v })}
      />
      <HStack gap={3} wrap="wrap" vAlign="end">
        <NumberInput
          label={intl.formatMessage({ id: 'alerts.cooldown' })}
          value={draft.cooldownMinutes}
          onChange={(v: number) => setDraft((d) => ({ ...d, cooldownMinutes: Math.max(1, Math.round(v)) }))}
        />
        {mode === 'team' ? (
          <MultiSelector
            formatValue={selectedText}
            label={intl.formatMessage({ id: 'alerts.channels' })}
            triggerDisplay="labels"
            value={draft.channels ?? []}
            onChange={(v: string[]) => setDraft((d) => ({ ...d, channels: v as AlertChannel[] }))}
            options={alertChannels.map((c) => ({
              value: c,
              label: intl.formatMessage({ id: `alerts.channel.${c}` }),
            }))}
          />
        ) : null}
      </HStack>
      <Switch
        label={intl.formatMessage({ id: 'alerts.notifyRecovery' })}
        value={draft.notifyRecovery}
        onChange={(v) => setDraft((d) => ({ ...d, notifyRecovery: v }))}
      />
    </DialogForm>
  );
}
